import { type FC, useContext, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaSignInAlt, FaTimes } from 'react-icons/fa';
import { AuthContext } from '../../../contexts/auth/auth.context';
import { isValidPayroll, isValidPayrollInput } from '../../../../shared/utils/payroll.utils';
import '@Styles/impersonation-banner.css';

interface ImpersonationFormData {
  payrollNumber: string;
}

export const ImpersonationBanner: FC = () => {
  const {
    initialClaims,
    claims,
    isCrewAdmin,
    impersonate,
    stopImpersonation,
    isImpersonating,
    isError,
    impersonatedUser,
  } = useContext(AuthContext);

  const [hasAttemptedSupplant, setHasAttemptedSupplant] = useState<boolean>(false);
  const [cachedPayrollNumber, setCachedPayrollNumber] = useState<string>('');
  const prevIsErrorRef = useRef<boolean | undefined>(undefined);

  const { register, handleSubmit, watch, reset } = useForm<ImpersonationFormData>({
    mode: 'onChange',
    defaultValues: { payrollNumber: '' },
  });

  const employeeToSupplant = watch('payrollNumber');
  const payrollNumber = claims.employeeid;
  const initialPayrollNumber = initialClaims.employeeid;
  const isInputValid = isValidPayroll(employeeToSupplant);

  const displayValue = isImpersonating
    ? (payrollNumber ?? cachedPayrollNumber)
    : employeeToSupplant;

  const onSubmit = (data: ImpersonationFormData): void => {
    if (isImpersonating) return;
    setHasAttemptedSupplant(true);
    setCachedPayrollNumber(data.payrollNumber);
    impersonate(data.payrollNumber);
  };

  const onClearImpersonation = (): void => {
    if (!isImpersonating || !initialPayrollNumber) return;
    reset();
    setCachedPayrollNumber('');
    stopImpersonation();
  };

  useEffect(() => {
    const isErrorTransition = isError && prevIsErrorRef.current === false;

    if (isErrorTransition && hasAttemptedSupplant) {
      setHasAttemptedSupplant(false);
      setCachedPayrollNumber('');
      reset();
    }

    prevIsErrorRef.current = isError;
  }, [isError, hasAttemptedSupplant, reset]);

  useEffect(() => {
    if (isImpersonating && hasAttemptedSupplant) {
      setHasAttemptedSupplant(false);
      reset();
      (document.activeElement as HTMLElement)?.blur();
    }
  }, [isImpersonating, hasAttemptedSupplant, reset]);

  useEffect(() => {
    if (isImpersonating && payrollNumber && payrollNumber !== cachedPayrollNumber) {
      setCachedPayrollNumber(payrollNumber);
    }
  }, [isImpersonating, payrollNumber, cachedPayrollNumber]);

  if (!isCrewAdmin) return null;

  return (
    <div className="impersonation-banner">
      <div className="impersonation-banner__inner">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="impersonation-banner__grid">
            <div className="impersonation-banner__col-left">
              {isImpersonating && impersonatedUser?.professionalInfo?.currentFleet && (
                <span className="impersonation-banner__fleet-inline">
                  {impersonatedUser.professionalInfo.currentFleet}
                </span>
              )}
            </div>

            <div className="impersonation-banner__col-center">
              <div className="impersonation-banner__input-wrapper">
                {isImpersonating ? (
                  <>
                    <input
                      className="impersonation-banner__input--readonly"
                      type="number"
                      placeholder="Nómina"
                      maxLength={6}
                      readOnly
                      value={displayValue}
                    />
                    <button
                      type="button"
                      aria-label="Cancelar suplantación"
                      onClick={onClearImpersonation}
                      className="impersonation-banner__btn-cancel"
                    >
                      <FaTimes />
                    </button>
                  </>
                ) : (
                  <>
                    <input
                      className={[
                        'impersonation-banner__input',
                        employeeToSupplant.length > 0 && !isInputValid
                          ? 'impersonation-banner__input--invalid'
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      type="text"
                      {...register('payrollNumber', {
                        validate: (value) => {
                          if (!value) return true;
                          return isValidPayrollInput(value);
                        },
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                          const value = e.target.value;
                          if (!isValidPayrollInput(value)) {
                            e.target.value = value.slice(0, -1);
                          }
                        },
                      })}
                      placeholder="Nómina"
                      maxLength={6}
                    />
                    {isInputValid && (
                      <div className="impersonation-banner__btn-submit-wrapper">
                        <button
                          type="submit"
                          aria-label="Suplantar usuario"
                          className="impersonation-banner__btn-submit"
                        >
                          <FaSignInAlt />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="impersonation-banner__col-right">
              {isImpersonating && impersonatedUser?.professionalInfo?.jobRole?.desc ? (
                <span className="impersonation-banner__role-single">
                  {impersonatedUser.professionalInfo.jobRole.desc}
                </span>
              ) : isError ? (
                <span style={{ fontSize: '0.75rem', color: '#d7192d', fontWeight: 600 }}>
                  Nómina no válida
                </span>
              ) : null}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
